// Two accessible controls select one existing native Shopify option value.
// Prices, inventory and cart submission remain Shopify's native variant flow.
// 2026-10-06 luxury companion: luxury sizes come from the unlisted companion
// product (custom.luxury_companion, JSON emitted by ae-nudge-size-facts.liquid;
// falls back to /products/<handle>-luxury-vial.js) when it has them. Choosing
// one keeps the native picker on the same-size main variant (old luxury
// variant while it still exists, else standard vial) and swaps the companion
// variant id into this section's /cart/add request (fetch layer: product-form.js
// rebuilds its FormData after the submit event, so a submit-time input edit
// does not stick -- seen on preview theme 189058253021). With no companion the
// picker behaves exactly as before.
(() => {
  const ECONOMY='economy-screw-01',LUXURY='luxury-crimp-01';
  function info(sku){
    if(typeof sku!=='string'||/^SUPPLY-|^TOOLS-|-FULL-|-FILL-/i.test(sku))return null;
    const m=/^(.+?)(?:-(LUX01))?-(\d+(?:\.\d+)?)ML$/i.exec(sku);
    if(!m)return null;const size=Number(m[3]);
    if(![2,3,5,7.5,10,30].includes(size))return null;
    return {prefix:m[1],size,style:m[2]||size===3?LUXURY:ECONOMY};
  }
  const words={en:{size:'Size',vial:'Choose your vial',economy:'Standard vial included',luxury:'Upgrade to luxury vial',economyPhoto:'Standard screw-top vials',luxuryPhoto:'Luxury vial',economyDetail:'Screw-top vial',luxuryDetail:'Crimp-sealed pressurized sprayer',shell:'Resin shell with a glass inner vial',automatic:'Luxury vial included',economyOnly:'Standard vial included',soldOut:'Sold out',equal:'3 mL with the luxury vial and 5 mL with the standard vial have the same total price. Each price includes the vial shown.'},fr:{size:'Format',vial:'Choisissez votre fiole',economy:'Fiole standard incluse',luxury:'Passer à la fiole de luxe',economyPhoto:'Fioles standard à bouchon vissé',luxuryPhoto:'Fiole de luxe',economyDetail:'Fiole à bouchon vissé',luxuryDetail:'Vaporisateur pressurisé serti',shell:'Coque en résine avec fiole intérieure en verre',automatic:'Fiole de luxe incluse',economyOnly:'Fiole standard incluse',soldOut:'Épuisé',equal:'Le format de 3 ml avec fiole de luxe et celui de 5 ml avec fiole standard ont le même prix total. Chaque prix comprend la fiole présentée.'}};
  // Vial styles a size can be bought in: whatever the (merged) variants actually offer, so a new luxury
  // companion size (e.g. 30 mL) appears with no theme change. Falls back to the old fixed rule.
  function styles(size,variants){
    const found=[ECONOMY,LUXURY].filter(s=>(variants||[]).some(v=>v.info.size===size&&v.info.style===s));
    return found.length?found:size===3?[LUXURY]:[5,10].includes(size)?[ECONOMY,LUXURY]:[ECONOMY];
  }
  const companionCache=new Map();
  // One fetch wrapper for the page. A /cart/add FormData request from a picker's own section whose id is
  // that picker's anchored main variant is sent as the chosen companion variant instead. Nothing else is touched.
  const pickers=new Set();
  // 2026-10-07 walkthrough fix: the chosen companion survives a section re-render (keyed by section id),
  // and the add request is rewritten from the picker's current choice whenever its id is any variant of
  // this product. Before, a quick Add right after picking Luxury could still send the standard variant.
  const remembered=new Map();
  if(!window.__aeVialFetch&&typeof window.fetch==='function'){
    window.__aeVialFetch=true;const _fetch=window.fetch;
    window.fetch=function(input,init){
      try{
        const url=typeof input==='string'?input:input?.url||String(input||'');
        const body=init?.body;
        if(/\/cart\/add(\.js)?(\?|$)/.test(url.split('#')[0])&&body instanceof FormData){
          const id=String(body.get('id')||''),sid=String(body.get('section-id')||'');
          for(const p of pickers){
            if(!p.isConnected||!p.variants||(sid&&p.section().id!=='shopify-section-'+sid))continue;
            if(!p.ownsVariant(id))continue;
            const cur=p.selected();
            if(cur&&String(cur.id)!==id)body.set('id',String(cur.id));
            break;
          }
        }
      }catch{}
      return _fetch.apply(this,arguments);
    };
  }
  class VialChoice extends HTMLElement {
    connectedCallback(){queueMicrotask(()=>this.initialise());}
    disconnectedCallback(){pickers.delete(this);this.observer?.disconnect();this.priceObserver?.disconnect();if(this.changeHandler)this.picker?.removeEventListener('change',this.changeHandler);}
    section(){return this.closest('.shopify-section')||document;}
    sectionKey(){return this.section().id||'page';}
    ownsVariant(id){return [...(this.main||[]),...(this.variants||[])].some(v=>String(v.id)===String(id)||String(v.anchor?.id)===String(id));}
    remember(){if(this.override)remembered.set(this.sectionKey(),this.override);else remembered.delete(this.sectionKey());}
    async loadCompanion(){
      const tag=this.section().querySelector('script[data-ae-luxury-companion]')||document.querySelector('script[data-ae-luxury-companion]');
      if(tag){try{return JSON.parse(tag.textContent).variants||[];}catch{return [];}}
      const url=this.section().querySelector('[data-product-url]')?.dataset.productUrl;
      if(!url||/-luxury-vial$/.test(url))return [];
      if(!companionCache.has(url))companionCache.set(url,fetch(url+'-luxury-vial.js',{headers:{Accept:'application/json'}}).then(r=>r.ok?r.json():null).then(p=>p?.variants||[]).catch(()=>[]));
      return companionCache.get(url);
    }
    // Main variants, with each luxury size replaced by the companion's variant when it has one.
    // While an old main luxury variant still exists, it wins over a sold-out companion variant of the same size.
    merge(main){
      const oldLux=size=>main.find(m=>m.info.size===size&&m.info.style===LUXURY);
      const comp=(this.companionRaw||[]).map(v=>({...v,info:info(v.sku)})).filter(v=>v.info&&v.info.style===LUXURY&&(v.available||!oldLux(v.info.size)?.available));
      if(!comp.length)return main;
      const sizes=new Set(comp.map(v=>v.info.size));
      return main.filter(v=>!(v.info.style===LUXURY&&sizes.has(v.info.size))).concat(comp.map(v=>({...v,companion:true,anchor:main.find(m=>m.info.size===v.info.size&&m.info.style===LUXURY)||main.find(m=>m.info.size===v.info.size&&m.info.style===ECONOMY)||null})));
    }
    async initialise(){
      const picker=this.closest('variant-picker'),source=this.querySelector('[data-ae-vial-data]');
      if(!picker||!source)return;
      let data;try{data=JSON.parse(source.textContent);}catch{return;}
      this.companionRaw=await this.loadCompanion();
      this.main=data.variants.map(v=>({...v,info:info(v.sku)})).filter(v=>v.info);
      const variants=this.merge(this.main);
      const prefixes=new Set(variants.map(v=>v.info.prefix));
      if(prefixes.size!==1||![3,5,10].every(size=>variants.some(v=>v.info.size===size&&v.info.style===LUXURY)))return;
      // Retain the normal native picker as the no-script/unsupported fallback.
      this.picker=picker;this.data=data;this.variants=variants;this.fr=data.locale?.startsWith('fr');
      this.copy=words[this.fr?'fr':'en'];
      const form=picker.querySelector('form');if(!form)return;
      const native=form.querySelectorAll(':scope > fieldset,:scope > .variant-option');
      if(!native.length)return;
      this.native=Array.from(native);this.native.forEach(n=>n.classList.add('ae-vial-native-fallback'));
      this.content=this.querySelector('[data-ae-vial-controls]');
      this.observer?.disconnect();
      this.observer=new MutationObserver(()=>{
        if(this.content!==this.querySelector('[data-ae-vial-controls]'))this.content=this.querySelector('[data-ae-vial-controls]');
        this.render();
      });
      this.observer.observe(form,{subtree:true,childList:true,attributes:true,attributeFilter:['checked','data-current-checked','data-variant-id']});
      if(this.changeHandler)picker.removeEventListener('change',this.changeHandler);
      this.changeHandler=()=>queueMicrotask(()=>this.render());picker.addEventListener('change',this.changeHandler);
      pickers.add(this);
      if(!this.override&&remembered.has(this.sectionKey()))this.override=remembered.get(this.sectionKey());
      this.render();this.setAttribute('data-ready','true');
    }
    nativeSelected(){
      const input=this.picker.querySelector('input[type=radio]:checked');
      const select=this.picker.querySelector('select');
      const value=input?.value||select?.value;
      return this.main.find(v=>v.option1===value)||this.main.find(v=>String(v.id)===String(input?.dataset.variantId));
    }
    // The companion override only stands while the native picker still sits on the variant it was anchored to.
    activeOverride(){
      if(!this.override)return null;
      const n=this.nativeSelected();
      if(!n||String(n.id)!==String(this.override.anchorId)){this.override=null;this.remember();return null;}
      return this.override;
    }
    selected(){
      const ov=this.activeOverride();if(ov)return this.variants.find(v=>String(v.id)===String(ov.id))||ov;
      const n=this.nativeSelected();if(!n)return null;
      // Native sits on an old luxury variant that the companion replaces: show (and buy) the companion.
      const c=n.info.style===LUXURY&&this.variants.find(v=>v.companion&&v.info.size===n.info.size);
      if(c){this.override={...c,anchorId:n.id};this.remember();return c;}
      return this.variants.find(v=>String(v.id)===String(n.id));
    }
    choose(variant){
      if(!variant)return;
      if(variant.companion){
        const anchor=variant.anchor||this.nativeSelected();
        this.override={...variant,anchorId:anchor?.id};this.remember();
        if(variant.anchor&&String(variant.anchor.id)!==String(this.nativeSelected()?.id)){this.clickNative(variant.anchor);}
        else{if(this.content)delete this.content.dataset.signature;this.render();}
        return;
      }
      this.override=null;this.remember();
      if(String(variant.id)===String(this.nativeSelected()?.id)){if(this.content)delete this.content.dataset.signature;this.render();return;}
      this.clickNative(variant);
    }
    clickNative(variant){
      const inputs=Array.from(this.picker.querySelectorAll('input[type=radio]'));
      const input=inputs.find(n=>n.value===variant.option1||String(n.dataset.variantId)===String(variant.id));
      if(input){input.click();return;}
      const select=this.picker.querySelector('select');
      if(select&&Array.from(select.options).some(o=>o.value===variant.option1)){select.value=variant.option1;select.dispatchEvent(new Event('change',{bubbles:true}));}
    }
    // Section price follows the companion while it is chosen (same formats as snippets/price.liquid: money / ae-money-locale).
    syncPrice(){
      const ov=this.activeOverride(),el=this.section().querySelector('product-price .price__regular .price');
      if(!el)return;
      if(!this.priceObserver){this.priceObserver=new MutationObserver(()=>{if(this.activeOverride())this.syncPrice();});this.priceObserver.observe(this.closest('.shopify-section')||document.body,{subtree:true,childList:true,characterData:true});}
      if(!ov){if(this.priceShown!=null&&el.textContent.trim()===this.priceShown&&this.priceNative!=null)el.textContent=this.priceNative;this.priceShown=this.priceNative=null;return;}
      const d=Math.floor(ov.price/100),r=String(ov.price%100).padStart(2,'0');
      const text=this.fr?(ov.price%100===0?d+' $':d+','+r+' $'):'$'+d+'.'+r;
      if(el.textContent.trim()!==text){this.priceNative=el.textContent.trim();el.textContent=text;}
      this.priceShown=text;
    }
    money(cents){const whole=cents%100===0;return new Intl.NumberFormat(this.fr?'fr-CA':'en-CA',{style:'currency',currency:this.data.currency||'CAD',minimumFractionDigits:whole?0:2,maximumFractionDigits:whole?0:2}).format(cents/100);}
    button(label,selected,available,onClick){
      const b=document.createElement('button');b.type='button';b.className='ae-vial-button';b.setAttribute('role','radio');
      b.setAttribute('aria-checked',String(selected));b.disabled=!available;b.textContent=label;b.tabIndex=selected&&available?0:-1;
      if(!available)b.setAttribute('aria-label',label+' - '+this.copy.soldOut);
      b.addEventListener('click',onClick);return b;
    }
    group(label){const field=document.createElement('fieldset');field.className='ae-vial-group';field.setAttribute('role','radiogroup');const legend=document.createElement('legend');legend.textContent=label;field.appendChild(legend);field.addEventListener('keydown',e=>{if(!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(e.key))return;const buttons=Array.from(field.querySelectorAll('button:not(:disabled)'));const index=buttons.indexOf(document.activeElement);if(index<0)return;e.preventDefault();const next=e.key==='Home'?0:e.key==='End'?buttons.length-1:(index+(['ArrowLeft','ArrowUp'].includes(e.key)?-1:1)+buttons.length)%buttons.length;buttons[next].focus();buttons[next].click();});return field;}
    photo(style,size){
      const url=this.data.photos?.[style]?.[size]||this.data.photos?.[style]?.all;if(!url)return null;
      // Shopify CDN, or this store's own /cdn/shop/ path (what Liquid image_url returns).
      if(!/^https:\/\/cdn\.shopify\.com\//.test(url)&&url.indexOf('https://'+location.host+'/cdn/shop/')!==0)return null;
      const img=document.createElement('img');img.src=url;img.alt=style===LUXURY?this.copy.luxuryPhoto+' - '+size+(this.fr?' ml':' mL'):this.copy.economyPhoto;img.width=120;img.height=120;img.loading='lazy';img.className='ae-vial-photo';return img;
    }
    fallback(){
      this.picker?.querySelector('form')?.querySelectorAll(':scope > fieldset,:scope > .variant-option').forEach(n=>n.classList.remove('ae-vial-native-fallback'));
      this.removeAttribute('data-ready');this.content?.replaceChildren();if(this.content)delete this.content.dataset.signature;
    }
    render(){
      const source=this.querySelector('[data-ae-vial-data]');
      if(source&&source.textContent!==this.lastDataText){try{this.data=JSON.parse(source.textContent);this.main=this.data.variants.map(v=>({...v,info:info(v.sku)})).filter(v=>v.info);this.variants=this.merge(this.main);this.lastDataText=source.textContent;}catch{this.fallback();return;}}
      if(!this.content||!this.isConnected)return;
      const native=this.picker.querySelector('form')?.querySelectorAll(':scope > fieldset,:scope > .variant-option');
      const current=this.selected();
      if(!current||new Set(this.variants.map(v=>v.info.prefix)).size!==1||![3,5,10].every(size=>this.variants.some(v=>v.info.size===size&&v.info.style===LUXURY))){
        native?.forEach(n=>n.classList.remove('ae-vial-native-fallback'));
        this.removeAttribute('data-ready');this.content.replaceChildren();delete this.content.dataset.signature;return;
      }
      native?.forEach(n=>n.classList.add('ae-vial-native-fallback'));
      this.setAttribute('data-ready','true');
      const signature=JSON.stringify([current.id,this.data.locale,this.variants.map(v=>[v.id,v.available,v.price])]);
      if(this.content.dataset.signature===signature&&this.content.children.length)return;
      const previousFocus=this.content.contains(document.activeElement)?document.activeElement?.dataset.key:null;
      const {size,style}=current.info,c=this.copy;const sizes=this.group(c.size);
      for(const nextSize of [...new Set(this.variants.map(v=>v.info.size))].sort((a,b)=>a-b)){
        const nextStyle=styles(size,this.variants).length>1&&styles(nextSize,this.variants).includes(style)?style:styles(nextSize,this.variants)[0];
        const preferred=this.variants.find(v=>v.info.size===nextSize&&v.info.style===nextStyle);
        const v=preferred?.available?preferred:this.variants.find(v=>v.info.size===nextSize&&v.available)||preferred;
        const b=this.button(nextSize+(this.fr?' ml':' mL'),size===nextSize,Boolean(v?.available),()=>this.choose(v));b.dataset.key='size-'+nextSize;sizes.appendChild(b);
      }
      const vial=this.group(c.vial),allowed=styles(size,this.variants);
      if(allowed.length===1){
        const note=document.createElement('div');note.className='ae-vial-included';const image=this.photo(style,size);if(image)note.appendChild(image);
        const text=document.createElement('span');text.textContent=allowed[0]===LUXURY?c.automatic:c.economyOnly;note.appendChild(text);vial.appendChild(note);
      }else{
        const economy=this.variants.find(v=>v.info.size===size&&v.info.style===ECONOMY);
        for(const choice of allowed){
          const v=this.variants.find(v=>v.info.size===size&&v.info.style===choice);if(!v)continue;
          const b=this.button('',style===choice,v.available,()=>this.choose(v));b.dataset.key='vial-'+choice;
          const image=this.photo(choice,size);if(image)b.appendChild(image);
          const name=document.createElement('strong');name.textContent=choice===ECONOMY?c.economy:c.luxury;
          if(!v.available)b.setAttribute('aria-label',name.textContent+' - '+c.soldOut);
          const detail=document.createElement('small');detail.textContent=choice===ECONOMY?c.economyDetail:c.luxuryDetail+(size===5?' · '+c.shell:'');
          if(choice===LUXURY&&economy)name.textContent+=' +'+this.money(v.price-economy.price);
          b.append(name,detail);vial.appendChild(b);
        }
      }
      for(const group of [sizes,vial]){const enabled=Array.from(group.querySelectorAll('button:not(:disabled)'));if(enabled.length&&!enabled.some(b=>b.tabIndex===0))enabled[0].tabIndex=0;}
      const nodes=[sizes,vial];const three=this.variants.find(v=>v.info.size===3),five=this.variants.find(v=>v.info.size===5&&v.info.style===ECONOMY);
      if([3,5].includes(size)&&three?.price===five?.price){const note=document.createElement('p');note.className='ae-vial-price-note';note.textContent=c.equal;nodes.push(note);}
      this.content.replaceChildren(...nodes);
      this.content.dataset.signature=signature;
      this.syncPrice();
      if(previousFocus)this.content.querySelector('[data-key="'+previousFocus+'"]')?.focus({preventScroll:true});
    }
  }
  if(!customElements.get('ae-vial-choice'))customElements.define('ae-vial-choice',VialChoice);
})();
